import { useCallback, useEffect, useRef, useState } from "react";
import { JustificationEntry, saveJustifications } from "../../lib/api/w3-wizard";

type DeviationLevel = "MATCH" | "MINOR" | "MAJOR" | "ADDED" | "MISSING";

interface JustificationPanelProps {
  sessionId?: string;
  comparisons?: ComparisonItem[];
  divergences?: ComparisonItem[];
  justifications: Record<string, JustificationEntry>;
  onJustificationsChange?: (justifications: Record<string, JustificationEntry>) => void;
  onChange?: (justifications: Record<string, JustificationEntry>) => void;
  onSaveAndExit?: () => void;
  onBlur?: () => void;
}

interface ComparisonItem {
  plug_number?: number;
  deviation_level: DeviationLevel | string;
  deviation_notes?: string[];
  variance_approval_found?: boolean;
  variance_approval_reference?: string;
  planned_top_ft?: number;
  planned_bottom_ft?: number;
  planned_sacks?: number;
  actual_top_ft?: number;
  actual_bottom_ft?: number;
  actual_sacks?: number;
  [key: string]: unknown;
}

const DEVIATION_CONFIG: Record<string, { border: string; badge: string; dot: string }> = {
  MATCH: {
    border: "border-green-300 dark:border-green-700",
    badge: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    dot: "bg-green-500",
  },
  MINOR: {
    border: "border-amber-300 dark:border-amber-700",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  MAJOR: {
    border: "border-red-400 dark:border-red-700",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    dot: "bg-red-500",
  },
  ADDED: {
    border: "border-blue-300 dark:border-blue-700",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  MISSING: {
    border: "border-red-300 dark:border-red-600",
    badge: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
    dot: "bg-gray-400",
  },
};

function DeviationBadge({ level }: { level: string }) {
  const cfg = DEVIATION_CONFIG[level.toUpperCase()] ?? DEVIATION_CONFIG.MINOR;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {level}
    </span>
  );
}

function fmt(v: number | undefined): string {
  return v !== undefined ? v.toLocaleString() : "—";
}

export default function JustificationPanel({
  sessionId,
  comparisons,
  divergences: divergencesProp,
  justifications,
  onJustificationsChange,
  onChange,
  onSaveAndExit,
  onBlur,
}: JustificationPanelProps) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedOverrides, setExpandedOverrides] = useState<Set<string>>(new Set());

  // Accept either comparisons (filters non-MATCH) or pre-filtered divergences
  const divergences = divergencesProp ?? (comparisons ?? []).filter(
    (c) => c.deviation_level.toUpperCase() !== "MATCH"
  );

  // Accept either callback name
  const notifyChange = onJustificationsChange ?? onChange;
  const notifyExit = onSaveAndExit ?? onBlur;

  const resolvedCount = divergences.filter((c) => {
    const key = String(c.plug_number ?? "unknown");
    return justifications[key]?.resolved === true;
  }).length;

  const triggerAutoSave = useCallback(
    (updated: Record<string, JustificationEntry>) => {
      if (!sessionId) return; // parent handles saving when no sessionId
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setSaving(true);
        setSaveError(null);
        try {
          await saveJustifications(sessionId, updated);
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : "Auto-save failed");
        } finally {
          setSaving(false);
        }
      }, 1500);
    },
    [sessionId]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const updateNote = (key: string, note: string) => {
    const updated = {
      ...justifications,
      [key]: {
        ...justifications[key],
        note,
        resolved: justifications[key]?.resolved ?? false,
        resolved_by: justifications[key]?.resolved_by ?? "",
        resolved_at: justifications[key]?.resolved_at ?? null,
      },
    };
    notifyChange?.(updated);
    triggerAutoSave(updated);
  };

  const updateResolved = (key: string, resolved: boolean) => {
    const updated = {
      ...justifications,
      [key]: {
        ...justifications[key],
        note: justifications[key]?.note ?? "",
        resolved,
        resolved_by: justifications[key]?.resolved_by ?? "",
        resolved_at: resolved ? new Date().toISOString() : null,
      },
    };
    notifyChange?.(updated);
    triggerAutoSave(updated);
  };

  const updateOverride = (key: string, field: string, value: number | null) => {
    const updated = {
      ...justifications,
      [key]: {
        ...justifications[key],
        note: justifications[key]?.note ?? "",
        resolved: justifications[key]?.resolved ?? false,
        resolved_by: justifications[key]?.resolved_by ?? "",
        resolved_at: justifications[key]?.resolved_at ?? null,
        [field]: value,
      },
    };
    notifyChange?.(updated);
    triggerAutoSave(updated);
  };

  const toggleExcluded = (key: string) => {
    const wasExcluded = justifications[key]?.excluded ?? false;
    const updated = {
      ...justifications,
      [key]: {
        ...justifications[key],
        note: justifications[key]?.note ?? "",
        resolved: true, // Excluded plugs are auto-resolved
        resolved_by: justifications[key]?.resolved_by ?? "",
        resolved_at: new Date().toISOString(),
        excluded: !wasExcluded,
      },
    };
    notifyChange?.(updated);
    triggerAutoSave(updated);
  };

  const toggleOverrideExpanded = (key: string) => {
    setExpandedOverrides(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSaveAndExit = async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (sessionId) {
        await saveJustifications(sessionId, justifications);
      }
      notifyExit?.();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (divergences.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No divergences found — all plugs match.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="rounded-xl border border-gray-200 dark:border-white/[0.05] bg-white dark:bg-white/[0.03] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Divergence Justifications
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-800 dark:text-white/90">{resolvedCount}</span>
            {" of "}
            <span className="font-semibold text-gray-800 dark:text-white/90">{divergences.length}</span>
            {" resolved"}
          </span>
          {(() => {
            const excludedCount = divergences.filter(c => {
              const k = String(c.plug_number ?? "unknown");
              return justifications[k]?.excluded === true;
            }).length;
            return excludedCount > 0 ? (
              <span className="text-xs text-red-500 dark:text-red-400">
                ({excludedCount} excluded)
              </span>
            ) : null;
          })()}
          {saving && (
            <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Saving...
            </span>
          )}
          {!saving && saveError && (
            <span className="text-xs text-red-500 dark:text-red-400">{saveError}</span>
          )}
        </div>
      </div>

      {/* Divergence cards */}
      <div className="space-y-3">
        {divergences.map((comp) => {
          const key = String(comp.plug_number ?? "unknown");
          const entry = justifications[key];
          const isResolved = entry?.resolved === true;
          const level = comp.deviation_level.toUpperCase();
          const cfg = DEVIATION_CONFIG[level] ?? DEVIATION_CONFIG.MINOR;

          return (
            <div
              key={key}
              className={`rounded-xl border-l-4 border border-gray-200 dark:border-white/[0.05] bg-white dark:bg-white/[0.03] ${
                isResolved ? "border-l-green-500" : cfg.border
              } ${entry?.excluded ? "opacity-50" : ""}`}
            >
              <div className="px-4 py-3 space-y-3">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      Plug {comp.plug_number ?? "—"}
                    </span>
                    <DeviationBadge level={comp.deviation_level} />
                    {entry?.excluded && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        Excluded
                      </span>
                    )}
                    {comp.variance_approval_found && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Variance Approval Found
                        {comp.variance_approval_reference && ` — ${comp.variance_approval_reference}`}
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isResolved}
                      onChange={(e) => updateResolved(key, e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">Resolved</span>
                  </label>
                </div>

                {/* Depths/sacks summary */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] p-2">
                    <p className="font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Planned</p>
                    <p className="text-gray-700 dark:text-gray-300">
                      {comp.planned_top_ft !== undefined
                        ? `${fmt(comp.planned_bottom_ft)}–${fmt(comp.planned_top_ft)} ft`
                        : "—"}
                      {comp.planned_sacks !== undefined && `, ${fmt(comp.planned_sacks)} sacks`}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] p-2">
                    <p className="font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Actual</p>
                    <p className="text-gray-700 dark:text-gray-300">
                      {comp.actual_top_ft !== undefined
                        ? `${fmt(comp.actual_bottom_ft)}–${fmt(comp.actual_top_ft)} ft`
                        : "—"}
                      {comp.actual_sacks !== undefined && `, ${fmt(comp.actual_sacks)} sacks`}
                    </p>
                  </div>
                </div>

                {/* Deviation notes */}
                {comp.deviation_notes && comp.deviation_notes.length > 0 && (
                  <ul className="space-y-0.5">
                    {comp.deviation_notes.map((note, i) => (
                      <li key={i} className="text-xs text-gray-500 dark:text-gray-400">
                        - {note}
                      </li>
                    ))}
                  </ul>
                )}

                {/* AI Suggested badge */}
                {entry?.ai_suggested && !isResolved && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      AI Suggested
                      {entry.confidence !== undefined && (
                        <span className="opacity-75">
                          · {Math.round(entry.confidence * 100)}%
                        </span>
                      )}
                    </span>
                    {entry.source_type && entry.source_type !== "none" && (
                      <span className="text-xs text-purple-600 dark:text-purple-400 capitalize">
                        {entry.source_type.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                )}

                {/* Justification text area */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Justification Note
                  </label>
                  <textarea
                    value={entry?.note ?? ""}
                    onChange={(e) => updateNote(key, e.target.value)}
                    rows={2}
                    placeholder="Explain the deviation or reference a variance approval..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                  />
                </div>

                {/* Override Actual Values — collapsible */}
                <div>
                  <button
                    type="button"
                    onClick={() => toggleOverrideExpanded(key)}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${expandedOverrides.has(key) ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Override Actual Values
                  </button>

                  {expandedOverrides.has(key) && (
                    <div className="mt-2 grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Top (ft)</label>
                        <input
                          type="number"
                          value={entry?.depth_top_ft_override ?? comp.actual_top_ft ?? ''}
                          onChange={(e) => updateOverride(key, 'depth_top_ft_override', e.target.value === '' ? null : Number(e.target.value))}
                          placeholder={comp.actual_top_ft !== undefined ? String(comp.actual_top_ft) : '—'}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Bottom (ft)</label>
                        <input
                          type="number"
                          value={entry?.depth_bottom_ft_override ?? comp.actual_bottom_ft ?? ''}
                          onChange={(e) => updateOverride(key, 'depth_bottom_ft_override', e.target.value === '' ? null : Number(e.target.value))}
                          placeholder={comp.actual_bottom_ft !== undefined ? String(comp.actual_bottom_ft) : '—'}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Sacks</label>
                        <input
                          type="number"
                          value={entry?.sacks_override ?? comp.actual_sacks ?? ''}
                          onChange={(e) => updateOverride(key, 'sacks_override', e.target.value === '' ? null : Number(e.target.value))}
                          placeholder={comp.actual_sacks !== undefined ? String(comp.actual_sacks) : '—'}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Exclude plug toggle */}
                <button
                  type="button"
                  onClick={() => toggleExcluded(key)}
                  className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                    entry?.excluded
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {entry?.excluded ? 'This plug is excluded' : 'Exclude this plug'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save & Exit button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSaveAndExit}
          disabled={saving}
          className="px-6 py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving..." : "Save & Exit"}
        </button>
      </div>
    </div>
  );
}
